import { CheckoutOrderQuery } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconEdit, IconTruckDelivery } from '@tabler/icons-react';
import { setShippingMethodOnCheckoutOrderDocument } from '../graphql/mutations';
import { checkoutOrderDocument } from '../graphql/queries';
import { useEffect, useState } from 'react';
import { showNotification } from '@mantine/notifications';

export const OrderCheckoutShippingSelect = ({
  order,
}: {
  order: CheckoutOrderQuery['checkoutOrder'];
}) => {
  const theme = useMantineTheme();

  const [isEdit, setEdit] = useState(false);

  const [setShippingMethod] = useMutation(setShippingMethodOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  useEffect(() => {
    if (!order.address) return;
    if (order.shippingMethodId) return;
    if (order.availableShippingMethods.length === 0) return;

    const shippingMethod = order.availableShippingMethods.find((m) =>
      m.name.toLowerCase().includes('gls')
    );

    if (!shippingMethod) {
      showNotification({
        title: 'Automatický výber dopravy zlyhal',
        message: 'Nie je dostupná žiadna doprava obsahujúca GLS',
        color: 'red',
      });
      return;
    }

    setShippingMethod({
      variables: {
        orderId: order.id,
        shippingMethodId: shippingMethod.id,
      },
    });
  }, [
    order.address,
    order.availableShippingMethods,
    order.id,
    order.shippingMethodId,
    setShippingMethod,
  ]);

  if (!order.shippingMethodId || isEdit) {
    return (
      <Card p="md" withBorder component={Stack}>
        <Text fw={500}>Vyberte spôsob doručenia</Text>
        {!order.address ? (
          <Alert color="red">Najprv zadajte adresu doručenia!</Alert>
        ) : (
          order.availableShippingMethods.length === 0 && (
            <Alert color="red">Nie sú dostupné žiadne spôsoby doručenia!</Alert>
          )
        )}
        <Stack>
          {order.availableShippingMethods
            .filter((method) => method.name.toLowerCase().includes('gls'))
            .map(({ id, name, description, fee }) => (
              <Card
                key={id}
                p="md"
                withBorder
                style={{
                  cursor: 'pointer',
                  borderColor:
                    order.shippingMethodId === id ? theme.colors.blue[6] : theme.colors.dark[4],
                }}
                onClick={async () => {
                  await setShippingMethod({
                    variables: { orderId: order.id, shippingMethodId: id },
                  });
                  setEdit(false);
                }}
              >
                <Group justify="space-between">
                  <Group>
                    <IconTruckDelivery size={16} />
                    <Stack gap={0}>
                      <Text>{name}</Text>
                      <Text size="sm" c="dimmed">
                        {description}
                      </Text>
                    </Stack>
                  </Group>
                  {fee > 0 && <Text>{fee} €</Text>}
                </Group>
              </Card>
            ))}
        </Stack>
      </Card>
    );
  } else {
    const method = order.availableShippingMethods.find((m) => m.id === order.shippingMethodId);

    if (!method)
      return (
        <Card p="md" withBorder component={Stack}>
          <Text fw={500}>Zvolený spôsob doručenia</Text>
          <Alert color="red">
            Nepodarilo sa načítať zvolený spôsob doručenia! Kontaktujte nás.
          </Alert>
        </Card>
      );

    return (
      <Card p="md" withBorder component={Stack}>
        <Text fw={500}>Zvolený spôsob doručenia</Text>
        <Card
          p="md"
          withBorder
          style={{
            cursor: 'pointer',
            borderColor: theme.colors.blue[6],
          }}
        >
          <Group justify="space-between">
            <Group>
              <IconTruckDelivery size={16} />
              <Stack gap={0}>
                <Text>{method.name}</Text>
                <Text size="sm" c="dimmed">
                  {method.description}
                </Text>
              </Stack>
            </Group>
            {method.fee > 0 && <Text>{method.fee} €</Text>}
            {order.availableShippingMethods.filter(
              (m) => m.name.toLowerCase().includes('gls') && m.id !== order.shippingMethodId
            ).length > 0 && (
              <Button size="xs" leftSection={<IconEdit size={16} />} onClick={() => setEdit(true)}>
                Zmeniť
              </Button>
            )}
          </Group>
        </Card>
      </Card>
    );
  }
};
